import { BookOpen, Target, Trophy } from 'lucide-react';
import { memo } from 'react';

import { PROGRESS_INDICATORS } from '../constants';

interface ConceptHeaderProps {
  description: string;
  title: string;
}

// Icon mapping object
const iconComponents = {
  BookOpen,
  Target,
  Trophy,
} as const;

const ConceptHeader = memo(({ description, title }: ConceptHeaderProps) => {
  return (
    <header className="text-center mb-4 sm:mb-6 lg:mb-8 py-4 sm:py-6 lg:py-8">
      <div className="mb-4 sm:mb-6 lg:mb-8">
        <div className="text-4xl lg:text-6xl font-extrabold tracking-tight text-primary mb-4 sm:mb-5 lg:mb-6">
          <span className="neon-glow">Master</span>{' '}
          <span className="relative inline-block">
            <span
              aria-hidden="true"
              className="absolute left-0 top-0 py-1.5 gradient-text-hero blur-lg opacity-50 dark:opacity-75 before:content-[attr(data-text)]"
              data-text={title}
            />
            <h1 className="relative py-1.5 gradient-text-hero left-0 top-0">
              {title}
            </h1>
          </span>
        </div>
        <div className="max-w-4xl mx-auto">
          <p className="text-lg lg:text-xl text-secondary mb-4 sm:mb-6 lg:mb-8 leading-relaxed">
            {description}
          </p>

          <div className="flex flex-wrap justify-center gap-2 md:gap-4">
            {PROGRESS_INDICATORS.map(indicator => {
              const IconComponent =
                iconComponents[indicator.icon as keyof typeof iconComponents];
              return (
                <div
                  aria-label={`${indicator.title}: ${indicator.subtitle} - informational`}
                  className="flex flex-col items-center gap-1 md:gap-2 p-2 md:p-4 card-informational rounded-lg cursor-default opacity-80"
                  key={indicator.title}
                  role="img"
                >
                  <div className="flex items-center justify-center w-6 h-6 md:w-8 md:h-8 rounded-full card-informational">
                    <IconComponent
                      aria-hidden="true"
                      className={`w-4 h-4 md:w-5 md:h-5 ${indicator.iconColor}`}
                    />
                  </div>
                  <div className="text-center">
                    <div className="text-xs md:text-sm text-secondary font-medium">
                      {indicator.title}
                    </div>
                    <div className="text-xs text-tertiary">
                      {indicator.subtitle}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </header>
  );
});

ConceptHeader.displayName = 'ConceptHeader';

export default ConceptHeader;
